import { Card } from "primereact/card";
import Container from "../../components/Container";
import AnalysisContainer from "./AnalysisContainer";
import AssetsMultiSelect from "../../components/AssetsMultiSelect";
import Text from "../../components/Text";
import SubGroupMultiSelect from "../../components/SubGroupMultiSelect";
import GroupMultiSelect from "../../components/GroupMultiSelect";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import useAuthHttp from "../../hooks/useAuthHttp";
import { useState } from "react";
import { IAssetTypesResponse } from "../../models/asset";
import { Chart } from "primereact/chart";
import { months } from "../../utils/date";
import Currency from "../../components/Currency";
import { Message } from "primereact/message";
import { Accordion, AccordionTab } from "primereact/accordion";
import {
    trackAnalysisFilterApplied,
    useAnalysisViewTracking,
} from "../../analytics";

interface IProjectionHistory {
    month_reference: string;
    month_evolution_value: number;
    month_initial_balance: number;
    month_total_result: number;
}

interface IProjectionFuture {
    month_reference: string;
    month_evolution_value: number;
    month_initial_balance: number;
    month_total_result: number;
}

interface IProjectionAnalysis {
    user_id: string;
    projection_history_list: IProjectionHistory[];
    projection_future_list: IProjectionFuture[];
    projection_future_optimistic_list: IProjectionFuture[];
    projection_future_pessimistic_list: IProjectionFuture[];
}

interface IProjectionResponse {
    projection_analysis: IProjectionAnalysis;
}

interface IProjectionPageFilters {
    group: string[];
    subGroup: string[];
    assets: string[];
}

const ProjectionPage = () => {
    const authHttp = useAuthHttp();
    const defaultDates = [moment().add(-4, "months").toDate(), moment().toDate()];
    const [filters, setFilters] = useState<IProjectionPageFilters>({
        group: [],
        subGroup: [],
        assets: []
    });
    const [groupLabel, setGroupLabel] = useState<string>("");
    const [subGroupLabel, setSubGroupLabel] = useState<string>("");
    const [assetsLabel, setAssetsLabel] = useState<string>("");

    const projectionQuery = useQuery<IProjectionResponse>({
        queryKey: [
            "projection",
            moment(defaultDates[0]).format("YYYY-MM-DD"),
            moment(defaultDates[1]).format("YYYY-MM-DD"),
            filters.group.join(","),
            filters.subGroup.join(","),
            filters.assets.join(",")
        ],
        queryFn: async () => {
            const searchParams = new URLSearchParams();
            searchParams.append("start_date", moment(defaultDates[0]).format("YYYY-MM-DD"));
            searchParams.append("end_date", moment(defaultDates[1]).format("YYYY-MM-DD"));
            searchParams.append("asset_group_desc", filters.group.join(","));
            searchParams.append("asset_group_id", filters.subGroup.join(","));
            searchParams.append("asset_user_id_list", filters.assets.join(","));
            return authHttp.doGet<IProjectionResponse>(`analysis/projection?${searchParams.toString()}`);
        }
    });

    const assetListTypeQuery = useQuery({
        queryKey: ["assetListType"],
        queryFn: async () =>
            await authHttp.doGet<IAssetTypesResponse>('asset/list_types')
    });

    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--text-color');
    const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary');
    const surfaceBorder = documentStyle.getPropertyValue('--surface-border');

    const chartDataActual = {
        labels: [
            ...projectionQuery.data?.projection_analysis.projection_history_list.map(p => p.month_reference) ?? []
        ].map(monthReference => {
            const date = moment(monthReference, "YYYYMM").toDate();

            return `${months[date.getMonth()]} ${date.getFullYear()}`;
        }),
        datasets: [
            {
                label: 'Performance da carteira',
                data: [
                    ...projectionQuery.data?.projection_analysis.projection_history_list.map(p => p.month_total_result) ?? []
                ],
                fill: false,
                borderColor: documentStyle.getPropertyValue('--primary-color'),
                tension: 0.4
            }
        ]
    };


    const projection =  [...projectionQuery.data?.projection_analysis.projection_history_list ?? []];

    if (!projection.length) {
        projection.push({
            month_reference: moment().format("YYYYMM"),
            month_evolution_value: 0,
            month_initial_balance: 0,
            month_total_result: 0
        });
    }

    const chartDataProjection = {
        labels: [
            projection.length > 1 ? projection[projection.length - 1].month_reference : projection[0].month_reference,
            ...projectionQuery.data?.projection_analysis.projection_future_list.map(p => p.month_reference) ?? []
        ].map(monthReference => {
            const date = moment(monthReference, "YYYYMM").toDate();

            return `${months[date.getMonth()]} ${date.getFullYear()}`;
        }),
        datasets: [
            {
                label: 'Projeção',
                data: [
                    projection.length > 1 ? projection[projection.length - 1].month_total_result : projection[0].month_reference,
                    ...projectionQuery.data?.projection_analysis.projection_future_list.map(p => p.month_total_result) ?? []
                ],
                fill: false,
                borderColor: documentStyle.getPropertyValue('--primary-color'),
                tension: 0.4
            },
            {
                label: 'Otimista',
                data: [
                    projection.length > 1 ? projection[projection.length - 1].month_total_result : projection[0].month_reference,
                    ...projectionQuery.data?.projection_analysis.projection_future_optimistic_list.map(p => p.month_total_result) ?? []
                ],
                fill: false,
                borderColor: documentStyle.getPropertyValue('--green-500'),
                tension: 0.4
            },
            {
                label: 'Pessimista',
                data: [
                    projection.length > 1 ? projection[projection.length - 1].month_total_result : projection[0].month_reference,
                    ...projectionQuery.data?.projection_analysis.projection_future_pessimistic_list.map(p => p.month_total_result) ?? []
                ],
                fill: false,
                borderColor: documentStyle.getPropertyValue('--attention-color'),
                tension: 0.4
            }
        ]
    };
    
    useAnalysisViewTracking('projection', projectionQuery.isSuccess);

    return (
        <Container flexColumn fullWidth gap={3}>            
            <AnalysisContainer
                loading={projectionQuery.isLoading}
                analysisType="projection"
                showExport={{ filename: "projeção" }}
                filters={[
                    {
                        name: "Grupos",
                        content: <GroupMultiSelect 
                            value={filters.group} 
                            onChange={(value) => {
                                trackAnalysisFilterApplied('projection', 'group');
                                setFilters(old => ({ ...old, group: value }));
                            }} 
                            onChangeSelectedLabel={setGroupLabel}
                            assets={assetListTypeQuery.data?.asset_list ?? []}
                        />,
                        chip: { label: groupLabel, icon: "pi-tags" }
                    },
                    {
                        name: "Subgrupos",
                        content: <SubGroupMultiSelect 
                            selectedGroupFamily={filters.group}
                            value={filters.subGroup} 
                            onChange={(value) => {
                                trackAnalysisFilterApplied('projection', 'subgroup');
                                setFilters(old => ({ ...old, subGroup: value }));
                            }} 
                            onChangeSelectedLabel={setSubGroupLabel}
                            assets={assetListTypeQuery.data?.asset_list ?? []}
                        />,
                        chip: { label: subGroupLabel, icon: "pi-sitemap" }
                    },
                    {
                        name: "Ativos",
                        content: <AssetsMultiSelect
                            period={defaultDates}
                            value={filters.assets}
                            onChange={(value) => {
                                trackAnalysisFilterApplied('projection', 'assets');
                                setFilters(old => ({ ...old, assets: value }));
                            }}
                            onChangeSelectedLabel={setAssetsLabel}
                        />,
                        chip: { label: assetsLabel, icon: "pi-chart-bar" }
                    }
                ]}
            >
                <Container flexColumn fullWidth gap={3}>
                    <Message 
                        severity="warn"
                        text="As projeções aqui exibidas NÃO consistem em afirmações de que o resultado acontecerá no futuro, nossos cálculos são baseados no histórico de performance da sua carteira e não prevemos eventos econômicos ou de força maior que podem afetar diretamente o seu resultado. Utilize essa projeção como uma referência para saber se sua estratégia está te levando para o resultado que deseja."
                    />

                    <Container wrap gap={3} fullWidth>
                        <Container className="flex flex-1 bg-white border-1 border-gray-300 gap-2 p-3 border-round justify-center items-center" flexColumn>
                            <Text className="text-sm text-900">Projeção da carteira atual</Text>
                            <Currency value={projectionQuery.data?.projection_analysis.projection_future_list.map(p => p.month_total_result)?.[0] ?? 0}>
                                {(value) => (
                                    <Text className="font-bold text-900 text-lg" color="primary">{value}</Text>
                                )}
                            </Currency>
                        </Container>

                        <Container className="flex flex-1 bg-white border-1 border-gray-300 gap-2 p-3 border-round justify-center items-center" flexColumn>
                            <Text className="text-sm text-900">Projeção Otimista</Text>
                            <Currency value={projectionQuery.data?.projection_analysis.projection_future_optimistic_list.map(p => p.month_total_result)?.[0] ?? 0}>
                                {(value) => (
                                    <Text className="font-bold text-lg" color="green-500">{value}</Text>
                                )}
                            </Currency>
                        </Container>

                        <Container className="flex flex-1 bg-white border-1 border-gray-300 gap-2 p-3 border-round justify-center items-center" flexColumn>
                            <Text className="text-sm text-900">Projeção Pessimista</Text>
                            <Currency value={projectionQuery.data?.projection_analysis.projection_future_pessimistic_list.map(p => p.month_total_result)?.[0] ?? 0}>
                                {(value) => (
                                    <Text className="font-bold text-lg" color="attention">{value}</Text>
                                )}
                            </Currency>
                        </Container>
                    </Container>

                    <Card
                        title="Projeção da Carteira"
                        subTitle="Simulação baseada no histórico de performance da sua carteira"
                        className="w-full"
                    > 
                        <Container className="p-accordion-fixed-income mb-4" fullWidth>
                            <Accordion>
                                <AccordionTab header="Carteira" className="bg-green-100">
                                    <Chart 
                                        type="line" 
                                        data={chartDataActual} 
                                        options={{
                                            maintainAspectRatio: false,
                                            aspectRatio: 0.6,
                                            plugins: {
                                                legend: {
                                                    labels: {
                                                        color: textColor
                                                    }
                                                },
                                                tooltip: {
                                                    callbacks: {
                                                        label: function (context: any) {
                                                            const value: number = context.raw;

                                                            const formattedValue = new Intl.NumberFormat("pt-BR", {
                                                                style: "currency",
                                                                currency: "BRL",
                                                            }).format(value);
                                    
                                                            return ` ${formattedValue}`;
                                                        },
                                                    },
                                                }
                                            },
                                            scales: {
                                                x: {
                                                    ticks: {
                                                        color: textColorSecondary
                                                    },
                                                    grid: {
                                                        color: surfaceBorder
                                                    }
                                                },
                                                y: {
                                                    ticks: {
                                                        color: textColorSecondary,
                                                        callback: function (value: any) {
                                                            return new Intl.NumberFormat("pt-BR", {
                                                                style: "currency",
                                                                currency: "BRL",
                                                            }).format(value);
                                                        }
                                                    },
                                                    grid: {
                                                        color: surfaceBorder
                                                    }
                                                }
                                            }
                                        }} 
                                    />
                                </AccordionTab>
                            </Accordion>
                        </Container>

                        <Chart 
                            type="line" 
                            data={chartDataProjection} 
                            options={{
                                maintainAspectRatio: false,
                                aspectRatio: 0.6,
                                plugins: {
                                    legend: {
                                        labels: {
                                            color: textColor
                                        }
                                    },
                                    tooltip: {
                                        callbacks: {
                                            label: function (context: any) {
                                                const value: number = context.raw;

                                                const formattedValue = new Intl.NumberFormat("pt-BR", {
                                                    style: "currency",
                                                    currency: "BRL",
                                                }).format(value);
                        
                                                return ` ${formattedValue}`;
                                            },
                                        },
                                    }
                                },
                                scales: {
                                    x: {
                                        ticks: {
                                            color: textColorSecondary
                                        },
                                        grid: {
                                            color: surfaceBorder
                                        }
                                    },
                                    y: {
                                        ticks: {
                                            color: textColorSecondary,
                                            callback: function (value: any) {
                                                return new Intl.NumberFormat("pt-BR", {
                                                    style: "currency",
                                                    currency: "BRL",
                                                }).format(value);
                                            }
                                        },
                                        grid: {
                                            color: surfaceBorder
                                        }
                                    }
                                }
                            }} 
                        />
                    </Card>
                </Container>
            </AnalysisContainer>
        </Container>
    );
}

export default ProjectionPage;
